import { createSlice, current } from '@reduxjs/toolkit';
import { callThunk } from '~/api';
import * as apiList from '~/apiTs';

const initialState = {
  wholeLoading: false,
  fetching: false,
  requestIds: [],
  wholeLoadingMessage: null,
};

/**
 * APIリクエスト中の状態管理用Slice。
 * リクエスト中かどうか・ページ全体でローディングを表示するかどうかなどを管理。
 * 基本的にAPIリクエスト中はwholeLoadingもfetchingもtrueにすれば良いが、
 * 個別の要素だけにローディングを表示したい、かつ特定のボタンはdisabledにしたいなどの際には、
 * wholeLoadingはfalseにして、fetchingだけtrueにするなどして対応すれば良い。
 */
const apiRequestSlice = createSlice({
  name: 'apiRequest',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(callThunk.pending, (state, action) => {
      if (action.meta.arg?.skipLoading) {
        return state;
      }
      const { requestIds, wholeLoadingMessage } = current(state);

      return {
        wholeLoading:
          action.meta.arg?.wholeLoading !== undefined ? action.meta.arg.wholeLoading : true,
        fetching: true,
        requestIds: [...requestIds, action.meta?.requestId],
        wholeLoadingMessage: action.meta.arg?.wholeLoadingMessage ?? wholeLoadingMessage,
      };
    });

    builder.addCase(callThunk.fulfilled, (state, action) => {
      if (action.meta.arg?.skipLoading) {
        return state;
      }
      // 完了したcallThunkのrequestIdをrequestIds配列から削除していく。
      // fulfilledしたcallThunkのrequestIdが、全部なくなったらローディング削除
      // メッセージがある場合、store比較して削除メッセージ割り出すと連続で呼ばれる場合、変更値が拾えないので単数前提でnull or undefinedでなければメッセージは削除する
      const { requestIds, wholeLoadingMessage } = current(state);
      const newRequestIds = requestIds.filter((requestId) => requestId !== action.meta?.requestId);
      const wholeFulfilled = newRequestIds.length === 0;
      return {
        wholeLoading: !wholeFulfilled,
        fetching: !wholeFulfilled,
        requestIds: newRequestIds,
        wholeLoadingMessage: !action.meta.arg?.wholeLoadingMessage ? wholeLoadingMessage : null,
      };
    });

    builder.addCase(callThunk.rejected, () => {
      return {
        wholeLoading: false,
        fetching: false,
        requestIds: [],
        wholeLoadingMessage: null,
      };
    });

    // Create handling for api calls
    const pendingActionTypes = [];
    const excludedAction = ['/original_forms/questions_content/pending'];
    Object.entries(apiList).forEach(([, func]) => {
      if (
        func.pending &&
        !excludedAction.includes(func.pending.toString()) &&
        !pendingActionTypes.includes(func.pending.toString())
      ) {
        pendingActionTypes.push(func.pending.toString());
        builder.addCase(func.pending, (state, action) => {
          if (action.meta.arg?.skipLoading) {
            return state;
          }
          const { requestIds, wholeLoadingMessage } = current(state);

          return {
            wholeLoading:
              action.meta.arg?.wholeLoading !== undefined ? action.meta.arg.wholeLoading : true,
            fetching: true,
            requestIds: [...requestIds, action.meta?.requestId],
            wholeLoadingMessage: action.meta.arg?.wholeLoadingMessage ?? wholeLoadingMessage,
          };
        });
      }
    });

    const rejectActionTypes = [];
    Object.entries(apiList).forEach(([, func]) => {
      if (func.rejected && !rejectActionTypes.includes(func.rejected.toString())) {
        rejectActionTypes.push(func.rejected.toString());
        builder.addCase(func.rejected, () => {
          return {
            wholeLoading: false,
            fetching: false,
            requestIds: [],
            wholeLoadingMessage: null,
          };
        });
      }
    });

    const fulfilledActionTypes = [];
    Object.entries(apiList).forEach(([, func]) => {
      if (func.fulfilled && !fulfilledActionTypes.includes(func.fulfilled.toString())) {
        fulfilledActionTypes.push(func.fulfilled.toString());
        builder.addCase(func.fulfilled, (state, action) => {
          if (action.meta.arg?.skipLoading) {
            return state;
          }
          // 完了したcallThunkのrequestIdをrequestIds配列から削除していく。
          // fulfilledしたcallThunkのrequestIdが、全部なくなったらローディング削除
          // メッセージがある場合、store比較して削除メッセージ割り出すと連続で呼ばれる場合、変更値が拾えないので単数前提でnull or undefinedでなければメッセージは削除する
          const { requestIds, wholeLoadingMessage } = current(state);
          const newRequestIds = requestIds.filter(
            (requestId) => requestId !== action.meta?.requestId
          );
          const wholeFulfilled = newRequestIds.length === 0;
          return {
            wholeLoading: !wholeFulfilled,
            fetching: !wholeFulfilled,
            requestIds: newRequestIds,
            wholeLoadingMessage: !action.meta.arg?.wholeLoadingMessage ? wholeLoadingMessage : null,
          };
        });
      }
    });
  },
});

// reducerをexportする
export default apiRequestSlice.reducer;
